import React, { useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import { ArrowLeft, ArrowRight, MapPin, CheckCircle2 } from "lucide-react";
import { CASE_STUDIES } from "@/data/site";
import { Reveal, Overline } from "@/components/site/primitives";
import { useModal } from "@/components/site/Forms";

export default function CaseStudy() {
  const { slug } = useParams();
  const cs = CASE_STUDIES.find((c) => c.slug === slug);
  const { openInstitution } = useModal();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [slug]);

  if (!cs) {
    return (
      <main className="flex min-h-screen flex-col items-center justify-center bg-ink-0 px-5 text-center" data-testid="case-study-not-found">
        <p className="font-mono text-xs uppercase tracking-[0.3em] text-brand-cyan">404</p>
        <h1 className="mt-4 font-display text-4xl font-bold text-white sm:text-5xl lg:text-6xl">Case study not found.</h1>
        <Link to="/" className="mt-8 inline-flex items-center gap-2 rounded-full border border-white/15 bg-white/5 px-7 py-3.5 font-medium text-white transition-colors hover:border-brand-cyan/50">
          <ArrowLeft className="h-4 w-4" /> Back to Home
        </Link>
      </main>
    );
  }

  return (
    <main className="relative min-h-screen overflow-hidden bg-ink-0 pb-24 pt-32" data-testid="case-study-page">
      <div className="absolute inset-0 bg-grid opacity-20" />
      <div className="absolute inset-0 bg-radial-glow" />

      <div className="relative mx-auto max-w-5xl px-5 lg:px-8">
        <Reveal>
          <Link to="/#network" className="inline-flex items-center gap-2 text-sm text-white/50 transition-colors hover:text-brand-cyan" data-testid="case-study-back">
            <ArrowLeft className="h-4 w-4" /> All Case Studies
          </Link>
        </Reveal>

        <Reveal delay={0.05}>
          <div className="mt-8">
            <Overline>Case Study</Overline>
            <h1 className="mt-5 font-display text-4xl font-bold leading-[1.02] tracking-tight text-white sm:text-5xl lg:text-6xl" data-testid="case-study-title">
              {cs.name} <span className="text-gradient">× A3.</span>
            </h1>
            <p className="mt-3 flex items-center gap-1.5 font-mono text-xs uppercase tracking-wider text-white/45">
              <MapPin className="h-3.5 w-3.5 text-brand-cyan" /> {cs.city}
            </p>
            <p className="mt-6 max-w-2xl font-display text-xl text-white/80 md:text-2xl">{cs.tagline}</p>
            <p className="mt-5 max-w-2xl text-base leading-relaxed text-white/55">{cs.overview}</p>
          </div>
        </Reveal>

        <div className="mt-16">
          <Reveal><h2 className="font-display text-lg text-brand-cyan md:text-lg">What A3 Delivers</h2></Reveal>
          <div className="mt-6 grid grid-cols-1 gap-5 sm:grid-cols-2">
            {cs.deliverables.map((d, i) => (
              <Reveal key={d.title} delay={(i % 2) * 0.08}>
                <div className="h-full rounded-3xl border border-white/10 bg-ink-1 p-7 transition-colors hover:border-brand-cyan/40" data-testid="deliverable-card">
                  <span className="font-display text-3xl font-bold text-brand-cyan/25">{String(i + 1).padStart(2, "0")}</span>
                  <h3 className="mt-3 font-display text-xl font-semibold text-white">{d.title}</h3>
                  <p className="mt-2 text-sm leading-relaxed text-white/55">{d.desc}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>

        <div className="mt-16 grid grid-cols-1 gap-10 lg:grid-cols-2">
          <Reveal>
            <div className="rounded-3xl border border-white/10 bg-ink-1 p-8">
              <h2 className="font-display text-lg text-white md:text-lg">The Approach</h2>
              <div className="mt-6 space-y-4">
                {cs.approach.map((step, i) => (
                  <div key={step} className="flex items-center gap-4">
                    <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full border border-brand-cyan/40 font-mono text-xs text-brand-cyan">{i + 1}</span>
                    <span className="text-sm text-white/75">{step}</span>
                  </div>
                ))}
              </div>
            </div>
          </Reveal>
          <Reveal delay={0.1}>
            <div className="rounded-3xl border border-brand-cyan/25 bg-gradient-to-br from-brand-blue/15 to-ink-1 p-8">
              <h2 className="font-display text-lg text-white md:text-lg">Outcomes</h2>
              <div className="mt-6 space-y-4">
                {cs.outcomes.map((o) => (
                  <div key={o} className="flex items-start gap-3">
                    <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-brand-cyan" />
                    <span className="text-sm leading-relaxed text-white/75">{o}</span>
                  </div>
                ))}
              </div>
            </div>
          </Reveal>
        </div>

        <Reveal delay={0.1}>
          <div className="mt-20 rounded-3xl border border-white/10 bg-ink-1 p-10 text-center">
            <h2 className="font-display text-base text-white md:text-lg">Want results like this for your institution?</h2>
            <p className="mx-auto mt-3 max-w-md text-sm text-white/55">Talk to our team about building your growth engine — technology, marketing, calling and admissions under one roof.</p>
            <button onClick={openInstitution} className="mt-7 inline-flex items-center justify-center gap-2 rounded-full bg-gradient-to-r from-brand-blue to-brand-cyan px-8 py-3.5 font-semibold text-[#08120A] transition-shadow hover:shadow-[0_0_30px_rgba(198,255,58,0.45)]" data-testid="case-study-cta">
              Partner With A3 <ArrowRight className="h-4 w-4" />
            </button>
          </div>
        </Reveal>
      </div>
    </main>
  );
}
